import '@css/mapbox.css'
import mapboxConfig from './mapbox-original-config.json'

// Image loading service for lazy loading marker popup images
class ProjectImageLoader {
    constructor() {
        this.cache = new Map();
    }

    async loadProjectImage(projectData) {
        const cacheKey = projectData.id;
        
        // Return cached result if available
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        // Use the pre-generated transform URLs from Craft
        const imageUrls = projectData.imageUrls ? {
            hasImage: true,
            webp: projectData.imageUrls.webp,
            fallback: projectData.imageUrls.fallback
        } : {
            hasImage: false,
            webp: null,
            fallback: null
        };
        
        // Cache the result
        this.cache.set(cacheKey, imageUrls);
        

        
        return imageUrls;
    }

    preloadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    }
}

// Deferred Mapbox loader - loads dependencies only when needed
class DeferredMapboxLoader {
    constructor() {
        this.scriptsLoaded = false;
        this.cssLoaded = false;
        this.loadingPromise = null;
    }

    async loadMapboxDependencies() {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        this.loadingPromise = new Promise(async (resolve, reject) => {
            try {
                // Load CSS
                if (!this.cssLoaded) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
                    document.head.appendChild(link);
                    this.cssLoaded = true;
                }

                // Load JavaScript
                if (!this.scriptsLoaded && typeof mapboxgl === 'undefined') {
                    const script = document.createElement('script');
                    script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
                    script.onload = () => {
                        this.scriptsLoaded = true;
                        resolve();
                    };
                    script.onerror = () => {
                        reject(new Error('Failed to load Mapbox GL JS'));
                    };
                    document.head.appendChild(script);
                } else {
                    resolve();
                }
            } catch (error) {
                reject(error);
            }
        });

        return this.loadingPromise;
    }
}

// Interaction-gated loading.
//
// The interactive map is the heaviest asset on the page (mapbox-gl + the custom
// style + vector tiles parse for several seconds on the main thread). We show a
// cached static preview (the facade) and only boot the real map on the first
// genuine user intent: hover, touch, keyboard activation or focus. Synthetic
// performance crawlers (Lighthouse / Oh Dear) never do any of these, so the heavy
// bundle stays out of the page-load trace while real users get the map the moment
// they reach for it.
function initDeferredMapbox(containerId, accessToken, projects) {
    const mapContainer = document.getElementById('map-container');
    const mapElement = document.getElementById(containerId);
    const facade = document.getElementById('map-facade');

    if (!mapContainer || !mapElement) {
        console.error('Map container or element not found');
        return;
    }

    const loader = new DeferredMapboxLoader();
    const status = document.getElementById('map-status');
    let mapInitialized = false;

    async function activate() {
        if (mapInitialized) return;
        mapInitialized = true;

        // Only redirect focus on success if the button actually had it (keyboard
        // / click activation). Mouse-hover activation must not steal focus.
        const facadeHadFocus = facade && document.activeElement === facade;

        if (facade) {
            facade.classList.add('is-loading');
            facade.setAttribute('aria-busy', 'true');
        }
        if (status) status.textContent = 'Loading the interactive map…';

        try {
            await loader.loadMapboxDependencies();

            if (facade) facade.remove();
            mapElement.classList.remove('hidden');

            new MapboxMap(containerId, accessToken, projects);

            if (status) status.textContent = 'Interactive map loaded.';

            // The facade button was removed; keep keyboard focus in a sensible
            // place instead of dropping it to <body> (WCAG 2.4.3 Focus Order).
            if (facadeHadFocus) {
                mapElement.setAttribute('tabindex', '-1');
                mapElement.focus({ preventScroll: true });
            }
        } catch (error) {
            console.error('Failed to load map:', error);
            // Reset so the listeners (still attached) allow a retry on the next
            // interaction; surface the failure to both sighted and AT users.
            mapInitialized = false;
            if (facade) {
                facade.classList.remove('is-loading');
                facade.removeAttribute('aria-busy');
                facade.classList.add('has-error');
                facade.setAttribute('aria-label', 'Map failed to load. Activate to retry.');
                const hint = facade.querySelector('.map-facade__hint');
                if (hint) hint.textContent = 'Map failed to load. Try again.';
            }
            if (status) status.textContent = 'The map failed to load. Activate the preview to try again.';
        }
    }

    if (!facade) {
        // No facade in the DOM (unexpected) — fall back to loading immediately so
        // the map is never permanently missing.
        activate();
        return;
    }

    // Activate on the first genuine intent. 'focus' is deliberately excluded so a
    // keyboard user tabbing past the button does not involuntarily boot the map
    // (WCAG 3.2.1 On Focus); a native <button> already activates on Enter/Space by
    // firing 'click'. pointerenter/touchstart pre-fetch on hover/tap before the
    // click lands. Listeners stay attached so a failed load can be retried.
    const intentEvents = ['pointerenter', 'touchstart', 'click'];
    intentEvents.forEach((name) => facade.addEventListener(name, activate, { passive: true }));
}

// Export the deferred loader function
window.initDeferredMapbox = initDeferredMapbox;

// Self-init: read the project data + Mapbox token from the DOM instead of
// requiring an inline <script> to pass them as call arguments (so this page
// never needs 'unsafe-inline' in script-src for the map).
function bootFromDom() {
    const dataEl = document.getElementById('map-projects-data');
    const mapContainer = document.getElementById('map-container');
    if (!dataEl || !mapContainer) return;

    let projects = [];
    try {
        projects = JSON.parse(dataEl.textContent);
    } catch (error) {
        console.error('Failed to parse map projects data:', error);
    }

    const accessToken = mapContainer.dataset.mapboxToken || '';
    initDeferredMapbox('map', accessToken, projects);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootFromDom);
} else {
    bootFromDom();
}

// Mapbox Map Functionality
class MapboxMap {
    constructor(containerId, accessToken, projects = []) {
        this.containerId = containerId;
        this.accessToken = accessToken;
        this.projects = projects;
        this.map = null;
        this.imageLoader = new ProjectImageLoader();
        
        this.init();
    }
    
    init() {
        // Check if mapboxgl is available
        if (typeof mapboxgl === 'undefined') {
            console.error('Mapbox GL JS is not loaded. Please ensure the Mapbox GL JS script is included.');
            return;
        }
        
        if (!this.validateToken()) return;
        
        console.log('Mapbox access token loaded successfully');
        console.log('Projects loaded:', this.projects.length);
        
        this.createMap();
    }
    
    validateToken() {
        if (!this.accessToken || 
            this.accessToken === 'YOUR_MAPBOX_ACCESS_TOKEN' || 
            this.accessToken === '') {
            console.error('Mapbox access token is missing or invalid:', this.accessToken);
            throw new Error('Mapbox access token is required. Please check your .env file.');
        }
        return true;
    }
    
    calculateInitialView() {
        // Use the default center and zoom from the original style
        let initialCenter = [42, 28];
        let initialZoom = 4;

        return { center: initialCenter, zoom: initialZoom };
    }
    
    createMapStyle() {
        return mapboxConfig;
    }
    
    createMap() {
        try {
            mapboxgl.accessToken = this.accessToken;
            const { center, zoom } = this.calculateInitialView();

            this.map = new mapboxgl.Map({
                container: this.containerId,
                style: this.createMapStyle(),
                center: center,
                zoom: zoom,
                attributionControl: true,
                projection: 'globe'
            });

            // Add zoom controls to bottom left
            this.map.addControl(new mapboxgl.NavigationControl({
                showCompass: false,
                showZoom: true
            }), 'bottom-left');

            this.map.on('load', () => {
                this.setupGlobeAtmosphere();
                this.addProjectMarkers();
                this.setupClustering();
            });

            this.map.on('error', (e) => {
                console.error('Map error:', e);
            });

        } catch (error) {
            console.error('Map creation error:', error);
        }
    }
    
    setupGlobeAtmosphere() {
        // Configure globe atmosphere exactly as in main branch
        this.map.setFog({
            color: 'rgb(220, 235, 245)', // Lighter blue background
            'high-color': 'rgb(100, 136, 199)', // Softer upper atmosphere
            'horizon-blend': 0.1, // Slightly thicker atmosphere
            'space-color': '#0d2151', // Much lighter space color
            'star-intensity': 0.2 // Reduced star visibility
        });
    }
    
    addProjectMarkers() {
        const features = this.projects.map((project, index) => ({
            type: 'Feature',
            properties: {
                title: project.title,
                description: project.description,
                url: project.url,
                type: project.type,
                id: project.id,
                projectImage: project.projectImage,
                // Add properties for cluster aggregation
                is_action_area: project.type === 'action-area' ? 1 : 0,
                is_blue_marker: project.type !== 'action-area' ? 1 : 0
            },
            geometry: {
                type: 'Point',
                coordinates: project.coordinates
            }
        }));

        // Add source for clustering with custom cluster properties
        this.map.addSource('projects', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: features
            },
            cluster: true,
            clusterMaxZoom: 22, // Allow clustering at maximum zoom levels
            clusterRadius: 30, // Smaller radius for tighter clustering
            clusterProperties: {
                // Count different marker types in each cluster
                action_area_count: ['+', ['get', 'is_action_area']],
                blue_marker_count: ['+', ['get', 'is_blue_marker']]
            }
        });

        // Add cluster layer with dynamic colors based on marker types
        this.map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'projects',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': [
                    'case',
                    // If action_area_count is 0, then cluster contains only blue markers
                    ['==', ['get', 'action_area_count'], 0], '#00a9d6', // All blue markers = blue
                    '#d57b20' // Otherwise (all orange or mixed) = orange
                ],
                'circle-radius': [
                    'step',
                    ['get', 'point_count'],
                    20, // Default radius
                    5, 25,  // If point_count >= 5, radius = 25
                    10, 30  // If point_count >= 10, radius = 30
                ],
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        });

        // Add cluster count layer
        this.map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'projects',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 14
            },
            paint: {
                'text-color': '#ffffff'
            }
        });

        // Add individual markers
        this.map.addLayer({
            id: 'unclustered-point',
            type: 'circle',
            source: 'projects',
            filter: ['!', ['has', 'point_count']],
            paint: {
                'circle-color': [
                    'case',
                    ['==', ['get', 'type'], 'action-area'], '#d57b20',
                    '#00a9d6'
                ],
                'circle-radius': 15,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        });

        // Individual markers are now just dots (no numbers)

        this.setupMarkerInteractions();
    }
    
    setupClustering() {
        // Cluster click - show multi-project popup instead of zooming
        this.map.on('click', 'clusters', (e) => {
            const features = this.map.queryRenderedFeatures(e.point, {
                layers: ['clusters']
            });
            const clusterId = features[0].properties.cluster_id;
            const coordinates = features[0].geometry.coordinates.slice();
            
            // Get all projects in this cluster
            this.map.getSource('projects').getClusterLeaves(
                clusterId,
                Infinity, // Get all leaves, no limit
                0,
                (err, clusterFeatures) => {
                    if (err) {
                        console.error('Error getting cluster leaves:', err);
                        return;
                    }
                    
                    if (clusterFeatures && clusterFeatures.length > 0) {
                        this.showMultiProjectPopup(coordinates, clusterFeatures);
                    }
                }
            );
        });
    }
    
    createMultiProjectPopupContent(projects, currentIndex = 0, imageData = null, isLoading = false) {
        const project = projects[currentIndex];
        const properties = project.properties;
        const totalProjects = projects.length;
        
        let imageContent;
        
        if (isLoading && (properties.hasImage === 'true' || properties.hasImage === true)) {
            imageContent = `
                <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
            `;
        } else if (imageData && imageData.hasImage) {
            imageContent = `
                <picture>
                    <source srcset="${imageData.webp}" type="image/webp">
                    <img src="${imageData.fallback}" alt="${properties.title}" class="w-full h-auto" loading="eager">
                </picture>
            `;
        } else {
            imageContent = `<div class="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>`;
        }

        const prevDisabled = currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer';
        const nextDisabled = currentIndex === totalProjects - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer';

        return `
            <div class="relative bg-white shadow-lg overflow-hidden max-w-xs md:max-w-md w-full">
                <button class="popup-close absolute top-3 right-3 w-6 h-6 bg-red-900/10 hover:bg-red-900/20 rounded-full flex items-center justify-center text-red-900 font-bold text-lg cursor-pointer z-10 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
                
                <div class="p-2 md:p-4">
                    <div class="flex">
                        <div class="relative w-32 h-auto flex-shrink-0 hidden md:block">
                            ${imageContent}
                        </div>
                        <div class="flex-1 p-2 md:p-4 flex flex-col justify-between">
                            <div>
                                <h3 class="text-lg font-semibold text-pink-600 mb-3 leading-tight">${properties.title}</h3>
                                <p class="font-text text-sm md:text-base text-red-900 font-normal">${properties.description || 'No description available'}</p>
                            </div>
                            <a href="${properties.url}" class="project-link inline-flex items-center text-pink-600 font-text font-medium text-base mt-4 hover:text-red-900 no-underline transition-colors" data-project-id="${properties.id}" data-project-title="${properties.title}" data-project-type="${properties.type}">
                                Read more
                                <svg class="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
                
                ${totalProjects > 1 ? `
                <div class="bg-brand-yellow px-4 py-3 flex items-center justify-between">
                    <button class="popup-prev flex items-center px-3 py-2 rounded transition-colors ${prevDisabled === 'opacity-50 cursor-not-allowed' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-magenta hover:text-white cursor-pointer'}" ${currentIndex === 0 ? 'disabled' : ''}>
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                        </svg>
                        Previous
                    </button>
                    <span class="font-medium text-center">Project ${currentIndex + 1} of ${totalProjects}</span>
                    <button class="popup-next flex items-center px-3 py-2 rounded transition-colors ${nextDisabled === 'opacity-50 cursor-not-allowed' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-brand-magenta hover:text-white cursor-pointer'}" ${currentIndex === totalProjects - 1 ? 'disabled' : ''}>
                        Next
                        <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                        </svg>
                    </button>
                </div>
                ` : ''}
            </div>
        `;
    }

    showMultiProjectPopup(coordinates, projects, initialIndex = 0) {
        let currentIndex = initialIndex;
        let currentPopup = null;
        
        const showProject = async (index) => {
            const project = projects[index];
            const properties = project.properties;
            
            // Find the project data for image loading
            const projectData = this.projects.find(p => p.id == properties.id);
            
            // Track marker click
            if (projectData) {
                MapboxAnalytics.trackMarkerClick(projectData);
            }
            
            // Create popup with loading state for image
            const popupContent = this.createMultiProjectPopupContent(projects, index, null, true);
            
            if (currentPopup) {
                currentPopup.remove();
            }
            
            currentPopup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: true
            })
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(this.map);
            
            // Add event listeners
            this.setupPopupEventListeners(currentPopup, projects, index, showProject);
            
            // Load image if project has one
            if (projectData && (projectData.hasImage === 'true' || projectData.hasImage === true)) {
                try {
                    const imageData = await this.imageLoader.loadProjectImage(projectData);
                    
                    // Update popup with loaded image
                    const updatedContent = this.createMultiProjectPopupContent(projects, index, imageData, false);
                    currentPopup.setHTML(updatedContent);
                    
                    // Re-add event listeners after content update
                    this.setupPopupEventListeners(currentPopup, projects, index, showProject);
                    
                    // Preload image
                    if (imageData.hasImage && imageData.webp) {
                        this.imageLoader.preloadImage(imageData.webp).catch(() => {
                            if (imageData.fallback) {
                                this.imageLoader.preloadImage(imageData.fallback);
                            }
                        });
                    }
                } catch (error) {
                    console.error('Failed to load image for popup:', error);
                }
            }
        };
        
        // Show the first project
        showProject(currentIndex);
    }
    
    setupPopupEventListeners(popup, projects, currentIndex, showProject) {
        const popupElement = popup.getElement();
        
        // Close button
        const closeBtn = popupElement.querySelector('.popup-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => popup.remove());
        }
        
        // Previous button
        const prevBtn = popupElement.querySelector('.popup-prev');
        if (prevBtn && !prevBtn.disabled) {
            prevBtn.addEventListener('click', () => {
                if (currentIndex > 0) {
                    showProject(currentIndex - 1);
                }
            });
        }
        
        // Next button
        const nextBtn = popupElement.querySelector('.popup-next');
        if (nextBtn && !nextBtn.disabled) {
            nextBtn.addEventListener('click', () => {
                if (currentIndex < projects.length - 1) {
                    showProject(currentIndex + 1);
                }
            });
        }
        
        // Project link tracking
        const projectLink = popupElement.querySelector('.project-link');
        if (projectLink) {
            const projectData = this.projects.find(p => p.id == projects[currentIndex].properties.id);
            if (projectData) {
                projectLink.addEventListener('click', () => {
                    MapboxAnalytics.trackProjectLinkClick(projectData);
                });
            }
        }
    }
    
    findOverlappingProjects(coordinates, tolerance = 0.0001) {
        // Find all projects at the same or very close coordinates
        return this.projects.filter(project => {
            const [lng, lat] = project.coordinates;
            const [targetLng, targetLat] = coordinates;
            
            return Math.abs(lng - targetLng) <= tolerance && 
                   Math.abs(lat - targetLat) <= tolerance;
        });
    }

    createPopupContent(properties, imageData = null, isLoading = false) {
        let imageContent;
        
        if (isLoading && (properties.hasImage === 'true' || properties.hasImage === true)) {
            // Show loading state
            imageContent = `
                <div class="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                </div>
            `;
        } else if (imageData && imageData.hasImage) {
            // Show loaded image with WebP support
            imageContent = `
                <picture>
                    <source srcset="${imageData.webp}" type="image/webp">
                    <img src="${imageData.fallback}" alt="${properties.title}" class="w-full h-auto" loading="eager">
                </picture>
            `;
        } else {
            // Show placeholder for no image
            imageContent = `<div class="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>`;
        }

        return `
            <div class="relative bg-white shadow-lg overflow-hidden max-w-xs md:max-w-md w-full p-2 md:p-4">
                <button class="absolute top-3 right-3 w-6 h-6 bg-brand-maroon/10 hover:bg-brand-maroon/20 rounded-full flex items-center justify-center text-brand-maroon font-bold text-lg cursor-pointer z-10 transition-colors">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>
                <div class="flex">
                    <div class="relative w-32 h-auto flex-shrink-0 hidden md:block">
                        ${imageContent}
                    </div>
                    <div class="flex-1 p-2 md:p-4 flex flex-col justify-between">
                        <div>
                            <h3 class="text-lg font-semibold text-brand-magenta mb-3 leading-tight">${properties.title}</h3>
                            <p class="font-text text-sm md:text-base text-brand-maroon font-normal">${properties.description || 'No description available'}</p>
                        </div>
                        <a href="${properties.url}" class="project-link inline-flex items-center text-brand-magenta font-text font-medium text-base mt-4 hover:text-brand-maroon no-underline transition-colors" data-project-id="${properties.id}" data-project-title="${properties.title}" data-project-type="${properties.type}">
                            Read more
                            <svg class="ml-1 w-4 h-4 transition-transform group-hover:translate-x-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        `;
    }

    setupMarkerInteractions() {
        // Individual marker clicks
        this.map.on('click', 'unclustered-point', async (e) => {
            const coordinates = e.features[0].geometry.coordinates.slice();
            const properties = e.features[0].properties;

            // Ensure popup doesn't go off screen
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }

            // Check for overlapping projects at the same location
            const overlappingProjects = this.findOverlappingProjects(coordinates);
            
            if (overlappingProjects.length > 1) {
                // Multiple projects at same location - show multi-project popup
                const projectFeatures = overlappingProjects.map(project => ({
                    type: 'Feature',
                    properties: {
                        id: project.id,
                        title: project.title,
                        description: project.description,
                        url: project.url,
                        type: project.type,
                        hasImage: project.hasImage
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: project.coordinates
                    }
                }));
                
                // Find the index of the clicked project to start with
                const clickedIndex = projectFeatures.findIndex(p => p.properties.id == properties.id);
                this.showMultiProjectPopup(coordinates, projectFeatures, Math.max(0, clickedIndex));
                return;
            }

            // Single project - show regular popup
            const projectData = this.projects.find(project => project.id == properties.id);
            
            // Track marker click (popup opening)
            if (projectData) {
                MapboxAnalytics.trackMarkerClick(projectData);
            }
            
            // Create initial popup with loading state for image
            const popupContent = this.createPopupContent(properties, null, true);

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: true
            })
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(this.map);

            // Add custom close button functionality
            const closeBtn = popup.getElement().querySelector('button');
            if (closeBtn) {
                closeBtn.addEventListener('click', () => {
                    popup.remove();
                });
            }
            
            // Add project link tracking
            const projectLink = popup.getElement().querySelector('.project-link');
            if (projectLink && projectData) {
                projectLink.addEventListener('click', () => {
                    MapboxAnalytics.trackProjectLinkClick(projectData);
                });
            }

            // Load image if project has one
            if (projectData && (projectData.hasImage === 'true' || projectData.hasImage === true)) {
                try {
                    const imageData = await this.imageLoader.loadProjectImage(projectData);
                    
                    // Update the popup with the loaded image
                    const updatedContent = this.createPopupContent(properties, imageData, false);
                    popup.setHTML(updatedContent);
                    
                    // Re-add close button functionality after content update
                    const newCloseBtn = popup.getElement().querySelector('button');
                    if (newCloseBtn) {
                        newCloseBtn.addEventListener('click', () => {
                            popup.remove();
                        });
                    }
                    
                    // Re-add project link tracking after content update
                    const newProjectLink = popup.getElement().querySelector('.project-link');
                    if (newProjectLink && projectData) {
                        newProjectLink.addEventListener('click', () => {
                            MapboxAnalytics.trackProjectLinkClick(projectData);
                        });
                    }

                    // Preload the image for smooth display
                    if (imageData.hasImage && imageData.webp) {
                        this.imageLoader.preloadImage(imageData.webp).catch(() => {
                            // Fallback to JPEG if WebP fails
                            if (imageData.fallback) {
                                this.imageLoader.preloadImage(imageData.fallback);
                            }
                        });
                    }
                } catch (error) {
                    console.error('Failed to load image for popup:', error);
                    // Keep the popup as-is with no image
                }
            }
        });

        // Hover effects
        this.map.on('mouseenter', 'clusters', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', 'clusters', () => {
            this.map.getCanvas().style.cursor = '';
        });

        this.map.on('mouseenter', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });

        this.map.on('mouseleave', 'unclustered-point', () => {
            this.map.getCanvas().style.cursor = '';
        });
    }
    
    fitMapToMarkers() {
        if (this.projects.length === 0) {
            // No projects, keep default center
            return;
        }

        // Calculate bounds from all project coordinates
        let minLng = Infinity, maxLng = -Infinity;
        let minLat = Infinity, maxLat = -Infinity;

        this.projects.forEach(project => {
            const [lng, lat] = project.coordinates;
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
        });

        // Create bounds with padding
        const bounds = [
            [minLng - 0.5, minLat - 0.5], // Southwest coordinates
            [maxLng + 0.5, maxLat + 0.5]  // Northeast coordinates
        ];

        // Fit map to bounds with minimal animation
        this.map.fitBounds(bounds, {
            padding: 50, // 50px padding around edges
            maxZoom: 10, // Don't zoom in too much for single markers
            duration: 300 // Very short, smooth transition
        });
    }
}

// GTM/GA4 Tracking Helper Functions
class MapboxAnalytics {
    static trackMarkerClick(projectData) {
        if (typeof gtag === 'function') {
            gtag('event', 'marker_click', {
                event_category: 'map_interaction',
                event_label: projectData.title,
                project_id: projectData.id,
                project_type: projectData.type,
                project_title: projectData.title,
                custom_parameter_1: projectData.type, // For GTM custom dimensions
                value: 1
            });
            
            console.log('Tracked marker click:', projectData.title);
        }
    }
    
    static trackProjectLinkClick(projectData) {
        if (typeof gtag === 'function') {
            gtag('event', 'project_link_click', {
                event_category: 'map_interaction',
                event_label: projectData.title,
                project_id: projectData.id,
                project_type: projectData.type,
                outbound: true, // This is an external link
                value: 1
            });
        }
    }
}

// Export for use in templates
window.MapboxMap = MapboxMap;
window.MapboxAnalytics = MapboxAnalytics;

